import { ref } from "vue";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { Project, ModelDefinition } from "./types";

type RemoteProject = {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string | null;
  agent_ids: string[];
};

type RemoteThread = {
  id: string;
  project_id: string;
  title: string | null;
  model_id?: string | null;
  created_at?: string;
  last_message_at?: string | null;
};

const projects = ref<Project[]>([]);
const models = ref<ModelDefinition[]>([]);
const loaded = ref(false);
// Text typed in a "create thread" composer (AgentDirectory or ThreadView's
// "new" state) that should be sent automatically once the new thread mounts.
const pendingFirstMessage = ref<string | null>(null);

async function loadProjects(
  trpc: ReturnType<typeof useTrpc>,
): Promise<Project[]> {
  const remoteProjects =
    (await trpc.intelligence.listProjects.query()) as RemoteProject[];

  return Promise.all(
    remoteProjects.map(async (project) => {
      const remoteThreads = (await trpc.intelligence.listThreads.query({
        projectId: project.id,
      })) as RemoteThread[];
      return {
        id: project.id,
        name: project.name,
        icon: project.icon,
        color: (project.color as Project["color"]) ?? "neutral",
        description: project.description ?? "",
        agentIds: project.agent_ids ?? [],
        members: [],
        bindings: { r2Assets: [], artifactRepo: null, schedules: [] },
        threads: remoteThreads.map((thread) => ({
          id: thread.id,
          projectId: thread.project_id,
          title: thread.title ?? "Untitled thread",
          createdBy: "",
          lastMessageAt: thread.last_message_at ?? thread.created_at ?? "",
          preview: "",
          participantAgentIds: [],
          modelId: thread.model_id ?? null,
        })),
      };
    }),
  );
}

export function useIntelligenceStore() {
  const trpc = useTrpc();

  const refresh = async () => {
    const [remoteProjects, remoteModels] = await Promise.all([
      loadProjects(trpc),
      trpc.intelligence.listModels.query() as Promise<ModelDefinition[]>,
    ]);
    projects.value = remoteProjects;
    models.value = remoteModels;
    loaded.value = true;
  };

  const ensureLoaded = async () => {
    if (!loaded.value) await refresh();
  };

  const createThread = async (
    projectId: string,
    title?: string,
    modelId?: string | null,
    agentId?: string | null,
  ) => {
    const thread = (await trpc.intelligence.createThread.mutate({
      projectId,
      title,
      modelId: modelId ?? undefined,
      agentId: agentId ?? undefined,
    })) as {
      id: string;
      project_id: string;
      title: string | null;
      model_id?: string | null;
    };

    const project = projects.value.find((p) => p.id === projectId);
    if (project) {
      project.threads.unshift({
        id: thread.id,
        projectId: thread.project_id,
        title: thread.title ?? "Untitled thread",
        createdBy: "",
        lastMessageAt: "",
        preview: "",
        participantAgentIds: [],
        modelId: thread.model_id ?? modelId ?? null,
      });
    }

    return thread;
  };

  const touchThread = (
    projectId: string,
    threadId: string,
    at: string = new Date().toISOString(),
  ) => {
    const project = projects.value.find((p) => p.id === projectId);
    const thread = project?.threads.find((item) => item.id === threadId);
    if (thread) thread.lastMessageAt = at;
  };

  const updateThreadTitle = (
    projectId: string,
    threadId: string,
    title: string,
  ) => {
    const project = projects.value.find((p) => p.id === projectId);
    const thread = project?.threads.find((item) => item.id === threadId);
    if (thread) thread.title = title;
  };

  const renameThread = async (
    projectId: string,
    threadId: string,
    title: string,
  ) => {
    await trpc.intelligence.renameThread.mutate({ projectId, threadId, title });
    updateThreadTitle(projectId, threadId, title);
  };

  const deleteThread = async (projectId: string, threadId: string) => {
    await trpc.intelligence.deleteThread.mutate({ projectId, threadId });
    const project = projects.value.find((p) => p.id === projectId);
    if (project) {
      project.threads = project.threads.filter((item) => item.id !== threadId);
    }
  };

  return {
    projects,
    refresh,
    ensureLoaded,
    createThread,
    touchThread,
    updateThreadTitle,
    renameThread,
    deleteThread,
    pendingFirstMessage,
    models,
    loaded,
  };
}
